import { db } from '../firebase';
import firebase from 'firebase/app';
import { createGalleryImage } from './galleryService';
import { createWebNotification } from './webNotificationService';

export async function getNotifications({ page = 1, pageSize = 12, search = '', date = '' } = {}) {
  try {
    const snapshot = await db.collection('notifications').orderBy('createdAt', 'desc').get();
    let allNotifs = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt ? (data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt)) : new Date(),
        scheduledAt: data.scheduledAt ? (data.scheduledAt.toDate ? data.scheduledAt.toDate() : new Date(data.scheduledAt)) : null,
      };
    });

    // Search Filter (by title or body)
    if (search && search.trim()) {
      const q = search.trim().toLowerCase();
      allNotifs = allNotifs.filter((n) => {
        const title = (n.title || '').toLowerCase();
        const body = (n.body || '').toLowerCase();
        return title.includes(q) || body.includes(q);
      });
    }

    // Date Filter (matches YYYY-MM-DD)
    if (date && date.trim()) {
      allNotifs = allNotifs.filter((n) => {
        const createdDateStr = n.createdAt instanceof Date ? n.createdAt.toISOString().split('T')[0] : '';
        const scheduledDateStr = n.scheduledAt instanceof Date ? n.scheduledAt.toISOString().split('T')[0] : '';
        return createdDateStr === date || scheduledDateStr === date;
      });
    }

    const total = allNotifs.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const validPage = Math.max(1, Math.min(page, totalPages));
    const startIndex = (validPage - 1) * pageSize;
    const items = allNotifs.slice(startIndex, startIndex + pageSize);
    return { items, total, totalPages, page: validPage, pageSize };
  } catch (error) {
    console.error('Error fetching notifications:', error);
    const snapshot = await db.collection('notifications').get();
    let allNotifs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    return { items: allNotifs, total: allNotifs.length, totalPages: 1, page: 1, pageSize };
  }
}

export async function createNotification(data) {
  const isScheduled = Boolean(data.scheduledAt && new Date(data.scheduledAt) > new Date());

  // Count registered mobile/web users for accurate reach stats
  let userCount = 0;
  try {
    const usersSnap = await db.collection('users').get();
    userCount = usersSnap.size;
  } catch (e) {
    // fallback if permissions restrict
  }

  const docData = {
    title: data.title,
    body: data.body,
    imageUrl: data.imageUrl || null,
    actionUrl: data.actionUrl || null,
    buttonText: data.buttonText || null,
    data: {
      title: data.title,
      body: data.body,
      imageUrl: data.imageUrl || null,
      actionUrl: data.actionUrl || null,
      buttonText: data.buttonText || null,
      type: 'broadcast',
    },
    topic: data.topic || 'all',
    targetUserId: 'all',
    targetRole: 'all',
    isBroadcast: true,
    status: isScheduled ? 'scheduled' : 'sent',
    scheduledAt: data.scheduledAt ? firebase.firestore.Timestamp.fromDate(new Date(data.scheduledAt)) : null,
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    sentCount: userCount || 1,
  };

  const docRef = await db.collection('notifications').add(docData);

  // 1. Auto-save image to Image Gallery (gallery_images) if notification has an image
  if (data.imageUrl && data.saveToGallery !== false) {
    try {
      await createGalleryImage({
        title: data.title || 'Notification Banner',
        category: data.galleryCategory || 'General',
        description: data.body || '',
        imageUrl: data.imageUrl,
      });
    } catch (galleryErr) {
      console.warn('Auto-save notification image to gallery notice:', galleryErr);
    }
  }

  // 2. Synchronize to web_notifications so ALL Web App users also receive it immediately
  if (!isScheduled) {
    try {
      await createWebNotification({
        title: data.title,
        summary: data.body,
        content: data.body,
        category: 'Announcement',
        imageUrl: data.imageUrl || null,
        actionUrl: data.actionUrl || null,
        buttonText: data.buttonText || 'View',
        isPinned: false,
        isActive: true,
        sourcePushNotificationId: docRef.id,
      });
    } catch (webErr) {
      console.warn('Auto-sync to web_notifications notice:', webErr);
    }
  }

  return docRef.id;
}

export async function deleteNotification(id) {
  return db.collection('notifications').doc(id).delete();
}